import React, { useState } from 'react';

export function IndicatorConfigInputs(props) {
    const { indicatorType, config, onChange, indicatorDef, patterns } = props;
    if (!indicatorType || !config) return null;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            {Object.keys(config).filter(key => key !== 'id').map(key => {
                const isNumber = typeof indicatorDef?.config?.[key] === 'number' || typeof config[key] === 'number';
                const isBoolean = typeof indicatorDef?.config?.[key] === 'boolean' || typeof config[key] === 'boolean';

                if (isBoolean) {
                    return (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                            <label style={{ margin: 0, cursor: 'pointer' }}>
                                <input type="checkbox" checked={Boolean(config[key])} onChange={e => onChange(key, e.target.checked)} />
                                {' '}{key}
                            </label>
                        </span>
                    );
                }

                if (indicatorType === 'pattern' && key === 'patternType' && patterns) {
                    return (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                            {key}: <select value={config[key]} onChange={e => onChange(key, e.target.value)} style={{ padding: '2px 4px', fontSize: '12px' }}>
                                {patterns.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </span>
                    );
                }

                return (
                    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                        {key}: <input
                            type={isNumber ? "number" : "text"}
                            step={key === 'stdDev' ? '0.1' : 'any'}
                            style={{ width: isNumber ? (key === 'stdDev' ? '45px' : '55px') : '90px', padding: '2px 4px', fontSize: '12px' }}
                            value={config[key] ?? ''}
                            onChange={e => onChange(key, isNumber ? Number(e.target.value) : e.target.value)}
                        />
                    </span>
                );
            })}
        </span>
    );
}

function IndicatorSelector(props) {
    const { indicators, selectedIndicators, setSelectedIndicators, patterns, buttonText } = props;
    const [selectedIndicatorType, setSelectedIndicatorType] = useState('');
    const [selectedIndicatorConfig, setSelectedIndicatorConfig] = useState({});
    const [selectedIndicatorId, setSelectedIndicatorId] = useState('');
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    function handleIndicatorTypeChange(e) {
        const type = e.target.value;
        setSelectedIndicatorType(type);
        if (type) {
            const indicator = indicators.find(i => i.type === type);
            if (indicator) {
                setSelectedIndicatorConfig({ ...indicator.config });
                if (type === 'pattern') {
                    setSelectedIndicatorId(indicator.config.patternType);
                } else {
                    setSelectedIndicatorId(type + (indicator.config.period ?? ''));
                }
            }
        } else {
            setSelectedIndicatorConfig({});
            setSelectedIndicatorId('');
        }
    }

    function handleConfigChange(key, value) {
        const indicatorDef = indicators.find(i => i.type === selectedIndicatorType);
        const isNumber = typeof indicatorDef?.config[key] === 'number';
        const val = isNumber ? Number(value) : value;

        setSelectedIndicatorConfig(prev => ({
            ...prev,
            [key]: val
        }));

        if (key === 'period') {
            setSelectedIndicatorId(selectedIndicatorType + val);
        } else if (selectedIndicatorType === 'pattern' && key === 'patternType') {
            setSelectedIndicatorId(val);
        }
    }

    function addIndicator() {
        if (!selectedIndicatorType) return;
        const indicatorDef = indicators.find(i => i.type === selectedIndicatorType);
        let defaultId = selectedIndicatorType + (selectedIndicatorConfig.period ?? 1);
        if (selectedIndicatorType === 'pattern') {
            defaultId = selectedIndicatorConfig.patternType;
        }
        const newIndicator = { type: selectedIndicatorType, id: selectedIndicatorId || defaultId, location: indicatorDef.location, config: selectedIndicatorConfig };
        setSelectedIndicators([...selectedIndicators, newIndicator]);
        setSelectedIndicatorType('');
        setSelectedIndicatorId('');
    }

    function removeIndicator(id) {
        setSelectedIndicators(selectedIndicators.filter(i => i.id !== id));
    }

    function removeAllIndicators() {
        setSelectedIndicators([]);
    }

    function handleDragStart(e, index) {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
    }

    function handleDragEnter(e, index) {
        if (draggedItemIndex === null || draggedItemIndex === index) return;
        const newIndicators = [...selectedIndicators];
        const draggedItem = newIndicators[draggedItemIndex];
        newIndicators.splice(draggedItemIndex, 1);
        newIndicators.splice(index, 0, draggedItem);
        setSelectedIndicators(newIndicators);
        setDraggedItemIndex(index);
    }

    function handleDragEnd() {
        setDraggedItemIndex(null);
    }

    return (
        <div>
            <div>
                {selectedIndicators.map((indicator, index) => (
                    <span key={indicator.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        style={{ backgroundColor: '#efefef', padding: '3px 8px', borderRadius: '10px', margin: '2px', display: 'inline-block', cursor: 'move' }}>
                        {indicator.id} <span style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: '5px' }} onClick={() => removeIndicator(indicator.id)}>X</span>
                    </span>
                ))}
            </div>
            <div className='sameline'>
                <select onChange={handleIndicatorTypeChange} value={selectedIndicatorType}>
                    <option value="">Select Indicator</option>
                    {indicators.map(i => <option key={i.type} value={i.type}>{i.type}</option>)}
                </select>
                &nbsp;
                {selectedIndicatorType && <span>
                    ID: <input type="text" style={{ width: '80px' }} value={selectedIndicatorId} onChange={e => setSelectedIndicatorId(e.target.value)} />&nbsp;
                </span>}
                {selectedIndicatorType && (
                    <IndicatorConfigInputs
                        indicatorType={selectedIndicatorType}
                        config={selectedIndicatorConfig}
                        onChange={handleConfigChange}
                        indicatorDef={indicators.find(i => i.type === selectedIndicatorType)}
                        patterns={patterns}
                    />
                )}
                &nbsp;
                {selectedIndicatorType && <button className="btn btn-primary btn-sm" onClick={addIndicator}>{buttonText || "Add to chart"}</button>}
                {selectedIndicators.length > 0 && <>&nbsp;&nbsp;<button className="btn btn-secondary btn-sm" onClick={removeAllIndicators}>Clear All</button></>}
            </div>
        </div>
    );
}

export default IndicatorSelector;